import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { trim } from "lodash";
import {
  Flex,
  Box,
  Card,
  Button,
  TextInput,
  TextArea,
  P,
} from "orcs-design-system";

import useOnMount from "src/hooks/useOnMount";
import ErrorNotification from "src/components/ErrorNotification";
import ConfigTypeSelect from "./ConfigTypeSelect";

const validateFormValues = ({ name, description }) => {
  const errors = {};
  const trimmedName = trim(name);
  const trimmedDescription = trim(description);

  if (!trimmedName) {
    errors.name = "Name is required.";
  } else if (trimmedName.length > 512) {
    errors.name = "Name must be 512 characters or fewer.";
  }

  if (!trimmedDescription) {
    errors.description = "Description is required.";
  } else if (trimmedDescription.length > 2048) {
    errors.description = "Description must be 2048 characters or fewer.";
  }

  return errors;
};

const WorkspaceForm = ({
  onSubmit,
  isLoading,
  preferredUsername,
  submitError,
  onClearSubmitError,
}) => {
  const nameInputRef = useRef();
  const [formValues, setFormValues] = useState({
    name: `New Workspace`,
    description: `Created by ${preferredUsername} on ${new Date().toLocaleString(
      "en-AU"
    )}`,
  });
  const [fieldErrors, setFieldErrors] = useState({});

  useOnMount(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  });

  const onFieldChange = (name) => (e) => {
    setFormValues({
      ...formValues,
      [name]: e.target.value,
    });
    if (fieldErrors[name]) {
      setFieldErrors({
        ...fieldErrors,
        [name]: undefined,
      });
    }
    if (submitError) {
      onClearSubmitError?.();
    }
  };

  const onConfigTypeSelect = (option) => {
    setFormValues({
      ...formValues,
      configType: option,
    });
    if (submitError) {
      onClearSubmitError?.();
    }
  };

  const onFormSubmit = (e) => {
    e.preventDefault();
    const errors = validateFormValues(formValues);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      onClearSubmitError?.();
      return;
    }

    setFieldErrors({});
    onSubmit({
      ...formValues,
      name: trim(formValues.name),
      description: trim(formValues.description),
    });
  };

  return (
    <Card title="New workspace">
      <Box maxWidth="500px" width="100%">
        <Flex flexDirection="column" gap="r">
          <TextInput
            name="name"
            type="text"
            id="name"
            label="Name"
            mandatory
            fullWidth
            value={formValues.name}
            invalid={!!fieldErrors.name}
            disabled={isLoading}
            onChange={onFieldChange("name")}
            ref={nameInputRef}
            onFocus={(e) => {
              e.target.select();
            }}
          />
          {fieldErrors.name && <P color="danger">{fieldErrors.name}</P>}
          <ConfigTypeSelect
            selectedConfigType={formValues.configType}
            onConfigTypeSelect={onConfigTypeSelect}
            disabled={isLoading}
          />
          <TextArea
            fullWidth
            id="description"
            name="description"
            label="Description"
            mandatory
            value={formValues.description}
            invalid={!!fieldErrors.description}
            onChange={onFieldChange("description")}
            rows="3"
            disabled={isLoading}
          />
          {fieldErrors.description && (
            <P color="danger">{fieldErrors.description}</P>
          )}
          {submitError && (
            <ErrorNotification
              message={submitError}
              report={false}
              floating={false}
              closable={false}
            />
          )}
          <Button
            onClick={onFormSubmit}
            isLoading={isLoading}
            disabled={isLoading}
          >
            Create workspace
          </Button>
        </Flex>
      </Box>
    </Card>
  );
};

WorkspaceForm.propTypes = {
  isLoading: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onClearSubmitError: PropTypes.func,
  preferredUsername: PropTypes.string.isRequired,
  submitError: PropTypes.string,
};

export default WorkspaceForm;
